import React, { useState, Suspense, useCallback, memo, useMemo } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import { useTextReplacement } from '../hooks/useTextReplacement'
import Button from '../components/button'
import { ChevronRight } from 'react-feather'
import Lightbox from 'yet-another-react-lightbox'
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen'
import 'yet-another-react-lightbox/styles.css'
import useLockBodyScroll from '../hooks/useLockBodyScroll'

const PhotoGallerySection = styled.div`
  text-align: center;
`
const GalleryHeading = styled.h3`
  margin-bottom: 0.45em;
  font-size: calc(0.75vw + 1.2em);
  line-height: 1.25;
`
const GalleryDescription = styled.p`
  max-width: 767px;
  color: #555;
  margin: 0 auto;
  margin-bottom: 1.5em;
`

const BenefitsSection = styled.div``

const InstructionalText = styled.p`
  text-align: center;
  color: #717171;
  font-size: 0.8em;
  font-style: italic;
  margin-bottom: 0px !important;
`

const FlexContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  .pswp-thumbnails {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
  }

  .pswp-thumbnail,
  .thumbnail-img {
    flex: 0 0 12.5%;
    padding: 7px;
    mask-image: none;
    img[data-main-image] {
      box-shadow: 0 6px 12px -2px rgba(50, 50, 93, 0.25);
      border-radius: 2px;
    }
    @media (max-width: 992px) {
      flex: 0 0 33.333333333%;
    }
    @media (max-width: 767px) {
      flex: 0 0 50%;
    }
    img {
      margin: 0;
    }
  }
`

const ImageWrapper = styled.div`
  flex: 0 0 12.5%;
  padding: 7px;
  .thumbnail-img.gatsby-image-wrapper {
    box-shadow: 0 6px 12px -2px rgba(50, 50, 93, 0.25);
    border-radius: 8px;
    &:hover {
      box-shadow: 0 7px 15px -2px rgba(50, 50, 93, 0.3);
    }
    min-height: 112px;
  }

  @media (max-width: 992px) {
    flex: 0 0 33.333333333%;
    .thumbnail-img.gatsby-image-wrapper {
      min-height: 130px;
    }
  }
  @media (max-width: 767px) {
    flex: 0 0 50%;
  }
  img {
    margin: 0;
  }
`

const CustomLightbox = styled(Lightbox)`
  .yarl__portal_open {
    z-index: 9999;
    position: relative;
  }
  .react-modal {
    padding: 0 !important;
  }
  .yarl__slide {
    padding: 0 !important;
  }
  & img {
    margin: 0 !important;
  }
`

const replaceDimensions = (url, newWidth, newHeight) => {
  let parts = url.split('?')
  if (parts.length !== 2) {
    return url
  }

  let baseUrl = parts[0]
  let params = parts[1].split('&')

  let newParams = params.map((param) => {
    if (param.startsWith('w=')) {
      return 'w=' + newWidth
    } else if (param.startsWith('h=')) {
      return 'h=' + newHeight
    }
    return param
  })

  return baseUrl + '?' + newParams.join('&')
}

const generateThumbnailImage = (gatsbyImageData) => {
  return {
    backgroundColor: gatsbyImageData.backgroundColor,
    height: gatsbyImageData.height,
    layout: gatsbyImageData.layout,
    width: gatsbyImageData.width,
    images: {
      fallback: {
        sizes: gatsbyImageData.images.fallback.sizes,
        src: replaceDimensions(
          gatsbyImageData.images.sources[0].srcSet
            .split(',\n')[1]
            .split(' ')[0],
          300,
          238
        ),
        srcSet: gatsbyImageData.images.fallback.srcSet,
      },
      sources: [
        {
          sizes: gatsbyImageData.images.sources[0].sizes,
          srcSet: replaceDimensions(
            gatsbyImageData.images.sources[0].srcSet
              .split(',\n')[1]
              .split(' ')[0],
            300,
            238
          ),
        },
      ],
    },
  }
}

const MemoizedImageWrapper = memo(({ onClick, id, thumbnailImage, alt }) => (
  <ImageWrapper onClick={onClick} key={id}>
    <GatsbyImage
      image={getImage(thumbnailImage)}
      className="thumbnail-img"
      style={{ cursor: 'pointer' }}
      alt={alt}
      aspectRatio={1}
    />
  </ImageWrapper>
))

const PhotoGalleryComponent = (props) => {
  const { isLoading, customContainerClassName, headingClassName, city } = props

  const [lightboxIsOpen, setLightboxIsOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)

  const toggleLightbox = useCallback((selectedIndex) => {
    setLightboxIsOpen((prev) => !prev)
    setSelectedIndex(selectedIndex)
  }, [])

  const galleryAll = props.gallery.length > 0 ? props.gallery[0] : props.gallery

  const items = useMemo(
    () =>
      galleryAll.photos.map((item) => ({
        ...item,
        id: item.id,
        caption: 'GVD Renovations',
        src: item.fullSize.images.fallback.src,
        w: 1200,
        h: 900,
        alt: item.title,
        thumbnailImage: generateThumbnailImage(item.gatsbyImageData),
      })),
    [galleryAll.photos]
  )

  const personalizedTextRef = useTextReplacement()

  useLockBodyScroll(lightboxIsOpen)

  return (
    <div>
      <PhotoGallerySection
        className={
          customContainerClassName ? customContainerClassName : 'container'
        }
      >
        <GalleryHeading className={headingClassName}>
          <span
            ref={personalizedTextRef}
            dangerouslySetInnerHTML={{
              __html: galleryAll.title
                ? city
                  ? galleryAll.title.replace('{{city}}', city)
                  : galleryAll.title
                : 'No Gallery Title',
            }}
          />
        </GalleryHeading>
        {galleryAll.description && galleryAll.description.description ? (
          <GalleryDescription>
            {galleryAll.description.description}
          </GalleryDescription>
        ) : null}

        {galleryAll.benefits && galleryAll.benefits.benefits && (
          <BenefitsSection
            dangerouslySetInnerHTML={{ __html: galleryAll.benefits.benefits }}
          />
        )}

        <FlexContainer>
          {items.map(
            (
              {
                thumbnail,
                caption,
                id,
                fluid,
                alt,
                image,
                thumbnailImage,
                ...rest
              },
              j
            ) => (
              <MemoizedImageWrapper
                onClick={() => toggleLightbox(j)}
                key={id}
                id={id}
                thumbnailImage={thumbnailImage}
                alt={alt}
              />
            )
          )}
        </FlexContainer>

        <InstructionalText>Tap images to enlarge</InstructionalText>
        {typeof document !== `undefined` && (
          <Suspense
            fallback={createPortal(
              <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-75 z-20000 text-white text-2xl align-center items-center flex flex-col w-full h-full justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-16 w-16 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span className="block text-2xl font-bold mt-10">
                  Loading Gallery View
                </span>
              </div>,
              document.getElementById('___gatsby')
            )}
          >
            <CustomLightbox
              open={lightboxIsOpen}
              close={() => setLightboxIsOpen(false)}
              slides={items.map((item) => ({
                src: item.src,
                alt: item.alt,
                id: item.id,
              }))}
              index={selectedIndex}
              onIndexChange={setSelectedIndex}
              plugins={[Fullscreen]}
              render={{
                slide: ({ slide }) => (
                  <img
                    src={slide.src}
                    alt={slide.alt}
                    style={{
                      height: window.innerWidth > 1280 ? '100vh' : 'auto',
                    }}
                  />
                ),
              }}
            />
          </Suspense>
        )}
        <Button
          to={
            '/projects/' +
            (props.filters && props.filters.filter
              ? '?filter=' + props.filters.filter
              : '')
          }
          className="mt-5-i md:hidden lg:hidden xl:hidden"
        >
          <span className="flex items-center justify-center babtn">
            See More Recent Projects <ChevronRight />
          </span>
        </Button>
      </PhotoGallerySection>
    </div>
  )
}

export default PhotoGalleryComponent
